import { JobStatus, JobType } from 'models';

const TIMESTEP_MESSAGE = /Solving timestep (\d+) of (\d+)/;
const TIME_ESTIMATE_WINDOW_MS = 60_000;
const SIMULATION_TIME_LIMIT_MS = 15 * 60_000;

const JobHelpers = {
    swr: {
        logs: (jobId: string) => `jobs/${jobId}/logs`,
    },

    getJobTypeLabel: (type: JobType): string => {
        const labels: Record<JobType, string> = {
            simulation: 'Power Flow',
            faultSimulation: 'Fault',
            wireSizing: 'Wire Sizing',
        };

        return labels[type] || type;
    },

    isProcessingTooLong: (job: { status: string; createdAt: string }): boolean => {
        if (job.status === JobStatus.ERROR || job.status === JobStatus.DONE) return false;

        const createdAt = new Date(job.createdAt).getTime();
        const now = Date.now();
        const oneHourInMs = 60 * 60 * 1000;
        return now - createdAt > oneHourInMs;
    },

    getTimeEstimate: (
        logs: { message: string; createdAt?: string | null }[],
    ): { remaining: number; exceedsTimeLimit: boolean } | null => {
        const points = logs
            .flatMap(({ message, createdAt }) => {
                const match = message.match(TIMESTEP_MESSAGE);

                return match && createdAt
                    ? [{ timestep: Number(match[1]), total: Number(match[2]), time: new Date(createdAt).getTime() }]
                    : [];
            })
            .filter(({ timestep }) => timestep > 1);

        const last = points[points.length - 1];
        const windowStart = points.findIndex(({ time }) => last && time >= last.time - TIME_ESTIMATE_WINDOW_MS);
        const first = points[windowStart] === last ? points[windowStart - 1] : points[windowStart];
        const startedAt = logs[0]?.createdAt;

        if (!first || !last || first === last || !startedAt) return null;

        const timestepsPerMs = (last.timestep - first.timestep) / (last.time - first.time);

        if (!(timestepsPerMs > 0)) return null;

        const remaining = (last.total - last.timestep) / timestepsPerMs;
        const elapsed = last.time - new Date(startedAt).getTime();

        return { remaining, exceedsTimeLimit: elapsed + remaining > SIMULATION_TIME_LIMIT_MS };
    },

    formatRemainingTime: (ms: number): string => {
        const minutes = Math.ceil(ms / 60_000);

        if (ms < 60_000) return 'Less than a minute remaining';
        if (minutes < 60) return `About ${minutes} min remaining`;

        return `About ${Math.floor(minutes / 60)} h ${minutes % 60} min remaining`;
    },
};

export { JobHelpers };
